import React from 'react';
import {
  Card,
  Box,
  Grid,
  Typography,
  Stack,
  Chip,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Archive,
  PlayArrow,
  Analytics,
  CheckCircle,
  ArrowBack,
  Assignment,
} from '@mui/icons-material';
import { Shipment, ShipmentStatus } from '../../../types/Shipment.types';
import { useNavigate } from 'react-router';

interface StatusConfig {
  color:
    | 'warning'
    | 'info'
    | 'success'
    | 'error'
    | 'default'
    | 'primary'
    | 'secondary';
  icon: React.ReactElement;
  label: string;
}

interface ShipmentHeaderProps {
  shipment: Shipment;
  statusConfig: StatusConfig | null;
  isSaving: boolean;
  processing: boolean;
  downloadingFile?: boolean;
  onAcceptShipment: () => void;
  onAnalyzeShipment: () => void;
  onCompleteShipment: () => void;
  onGenerateReport?: () => void;
  totalScannedItems: number;
}

const ShipmentHeader: React.FC<ShipmentHeaderProps> = ({
  shipment,
  statusConfig,
  isSaving,
  processing,
  downloadingFile = false,
  onAcceptShipment,
  onAnalyzeShipment,
  onCompleteShipment,
  onGenerateReport,
  totalScannedItems,
}) => {
  const nav = useNavigate();

  return (
    <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
      <Box
        sx={(theme) => ({
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          p: { xs: 2, md: 3 },
        })}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* LEFT SIDE - Back Button + Title + Ref + Status */}
          <Grid item xs={12} md={6}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 1 }}
            >
              <IconButton
                onClick={() => nav(-1)}
                color="inherit"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: 'uppercase',
                }}
              >
                Shipment Reception
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography
                variant="subtitle1"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '1.05rem',
                  fontWeight: 500,
                }}
              >
                {shipment.refNumber}
              </Typography>

              {statusConfig && (
                <Chip
                  icon={statusConfig.icon}
                  label={statusConfig.label}
                  color={statusConfig.color}
                  variant="filled"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              )}

              {isSaving && (
                <Chip
                  icon={<CircularProgress size={16} color="inherit" />}
                  label="Saving..."
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}
                />
              )}
            </Stack>
          </Grid>

          {/* RIGHT SIDE - Action Buttons */}
          <Grid item xs={12} md={6}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              flexWrap="wrap"
            >
              {shipment.status === ShipmentStatus.Expected && (
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={onAcceptShipment}
                  size="large"
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  Accept Shipment
                </Button>
              )}

              {(shipment.status === ShipmentStatus.InReception ||
                shipment.status === ShipmentStatus.Received ||
                shipment.status === ShipmentStatus.Analyzed) && (
                <Button
                  variant="contained"
                  startIcon={
                    processing ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Analytics />
                    )
                  }
                  onClick={onAnalyzeShipment}
                  disabled={processing || totalScannedItems === 0}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  {processing ? 'Analyzing...' : 'Analyze Shipment'}
                </Button>
              )}

              {shipment.status === ShipmentStatus.Analyzed && (
                <>
                  {onGenerateReport && (
                    <Button
                      variant="outlined"
                      startIcon={
                        downloadingFile ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <Assignment />
                        )
                      }
                      onClick={onGenerateReport}
                      disabled={downloadingFile}
                      sx={{
                        fontWeight: 600,
                        color: 'white',
                        borderColor: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.2)',
                          borderColor: 'white'
                        },
                      }}
                    >
                      {downloadingFile ? 'Generating...' : 'Generate Report'}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={
                      processing ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CheckCircle />
                      )
                    }
                    onClick={onCompleteShipment}
                    disabled={processing}
                    sx={{
                      fontWeight: 600,
                      color: 'primary.main',
                      bgcolor: 'white',
                      '&:hover': { bgcolor: 'grey.100' },
                      background: 'linear-gradient(45deg, #fff 30%, #f5f5f5 90%)',
                      border: '2px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    {processing ? 'Completing...' : 'Complete Shipment'}
                  </Button>
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default ShipmentHeader;
